import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import PhotoUploadButton from "@/components/PhotoUploadButton";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedStorageUrl } from "@/lib/storageObjectUrls";
import { toast } from "sonner";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppLoadingState,
  AppText,
  AppTextArea,
} from "../../ui-foundation";

const emptyForm = {
  title: "",
  description: "",
  event_date: "",
  location: "",
  is_free: true,
  cost: "",
  url: "",
};

const toDateTimeLocalValue = (value: string) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const CreateEvent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const resolvedImageUrl = useResolvedStorageUrl("event-photos", imageUrl);

  useEffect(() => {
    if (!id) {
      setLoadingEvent(false);
      return;
    }

    let active = true;

    const loadEvent = async () => {
      setLoadingEvent(true);
      const { data, error } = await supabase.from("events").select("*").eq("id", id).maybeSingle();

      if (!active) return;

      if (error || !data) {
        toast.error("Couldn't load event");
        navigate("/activities?section=whats-on", { replace: true });
        return;
      }

      setForm({
        title: data.title ?? "",
        description: data.description ?? "",
        event_date: data.event_date ? toDateTimeLocalValue(data.event_date) : "",
        location: data.location ?? "",
        is_free: data.is_free ?? true,
        cost: data.cost ?? "",
        url: data.url ?? "",
      });
      setImageUrl(data.image_url ?? null);
      setLoadingEvent(false);
    };

    void loadEvent();

    return () => {
      active = false;
    };
  }, [id, navigate]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.event_date || !form.location.trim() || !user || (!isEditing && !form.url.trim())) {
      return;
    }

    setSubmitting(true);

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || "Shared by a member for admin review.",
      event_date: new Date(form.event_date).toISOString(),
      location: form.location.trim(),
      is_free: form.is_free,
      cost: form.is_free ? null : form.cost.trim() || null,
      url: form.url.trim() || null,
      image_url: imageUrl,
    };

    const { error } = isEditing && id
      ? await supabase.from("events").update(payload).eq("id", id)
      : await supabase.from("events").insert({
          ...payload,
          is_published: false,
          submitted_by: user.id,
        });

    setSubmitting(false);

    if (error) {
      toast.error(isEditing ? "Couldn't update event" : "Couldn't submit event");
      return;
    }

    toast.success(
      isEditing
        ? "Event updated"
        : "Thanks! Your local event link is now waiting for review. We’ll publish it once it is approved.",
    );
    navigate("/activities?section=whats-on");
  };

  const titleText = isEditing ? "Edit submission" : "Share a local event";
  const subtitle = isEditing
    ? "Update the event link or notes before the team reviews it."
    : "Paste a public event link for something useful near you. Kindred reviews member submissions before publishing them in What’s On.";

  if (loadingEvent) {
    return (
      <MemberPageScaffold title={titleText} subtitle={subtitle} fallbackPath="/activities">
        <AppLoadingState title="Loading event" body="Pulling in the details you saved earlier." />
      </MemberPageScaffold>
    );
  }

  return (
    <MemberPageScaffold title={titleText} subtitle={subtitle} fallbackPath="/activities">
      <AppCard>
        <AppCardBody gap="$md">
          <AppFormSection
            title={isEditing ? "Event details" : "Event link and basics"}
            description={
              isEditing
                ? "Update the key details before review."
                : "Start with the public event link, then add the basics so the team can review and publish it."
            }
          >
            <AppFieldGroup
              label={isEditing ? "URL for more info" : "Event link *"}
              helper={isEditing ? "Useful for booking links or venue details." : "Paste the public listing, venue, or booking link."}
            >
              <AppInput
                value={form.url}
                onChangeText={(value) => setForm({ ...form, url: value })}
                placeholder="https://..."
              />
            </AppFieldGroup>

            <AppFieldGroup label="Title *">
              <AppInput
                value={form.title}
                onChangeText={(value) => setForm({ ...form, title: value })}
                placeholder="Give the event a clear name"
              />
            </AppFieldGroup>

            <AppFieldGroup label={isEditing ? "Description" : "Optional note for admin"}>
              <AppTextArea
                value={form.description}
                onChangeText={(value) => setForm({ ...form, description: value })}
                placeholder={
                  isEditing
                    ? "What is happening, who is it for, and what should families know first?"
                    : "Anything helpful the team should know before review."
                }
              />
            </AppFieldGroup>

            <AppFieldGroup label="Date and time *">
              <AppInput
                type="datetime-local"
                value={form.event_date}
                onChange={(event) => setForm({ ...form, event_date: event.target.value })}
              />
            </AppFieldGroup>

            <AppFieldGroup label="Location *">
              <AppInput
                value={form.location}
                onChangeText={(value) => setForm({ ...form, location: value })}
                placeholder="Town or city"
              />
            </AppFieldGroup>
          </AppFormSection>

          <AppFormSection title="Pricing and extras">
            <AppFieldGroup label="Price type">
              <AppButtonRow>
                <AppButton
                  type="button"
                  tone={form.is_free ? "secondary" : "outline"}
                  size="sm"
                  onPress={() => setForm({ ...form, is_free: true, cost: "" })}
                >
                  Free event
                </AppButton>
                <AppButton
                  type="button"
                  tone={!form.is_free ? "secondary" : "outline"}
                  size="sm"
                  onPress={() => setForm({ ...form, is_free: false })}
                >
                  Paid event
                </AppButton>
              </AppButtonRow>
            </AppFieldGroup>

            {!form.is_free ? (
              <AppFieldGroup label="Cost">
                <AppInput
                  value={form.cost}
                  onChangeText={(value) => setForm({ ...form, cost: value })}
                  placeholder="e.g. GBP5 per family"
                />
              </AppFieldGroup>
            ) : null}

            <AppFormSection
              title="Cover image"
              description="Optional, but it helps the card stand out in What&apos;s On."
            >
              <div className="overflow-hidden rounded-[24px] border border-border bg-card">
                {resolvedImageUrl ? (
                  <img src={resolvedImageUrl} alt="" className="h-44 w-full object-cover" />
                ) : (
                  <div className="flex h-44 items-end bg-gradient-to-br from-secondary/15 via-background to-secondary/5 p-4">
                    <span className="rounded-full bg-card/80 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                      Optional cover image
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <PhotoUploadButton
                  bucket="event-photos"
                  onPhotoReady={setImageUrl}
                  onClear={() => setImageUrl(null)}
                  previewUrl={null}
                  buttonLabel={imageUrl ? "Change image" : "Upload image"}
                  returnObjectPath
                />
                <div className="min-w-0 flex-1">
                  <AppText fontSize="$sm" fontWeight="600">
                    {imageUrl ? "Replace cover image" : "Upload cover image"}
                  </AppText>
                  <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                    This shows on the event card and detail page.
                  </AppText>
                </div>
                {imageUrl ? (
                  <AppButton type="button" tone="outline" size="sm" onPress={() => setImageUrl(null)}>
                    Remove
                  </AppButton>
                ) : null}
              </div>
            </AppFormSection>
          </AppFormSection>

          <AppButtonRow>
            <AppButton
              type="button"
              tone="outline"
              onPress={() => navigate("/activities?section=whats-on")}
              flex={1}
            >
              Cancel
            </AppButton>
            <AppButton
              type="button"
              onPress={handleSubmit}
              disabled={!form.title.trim() || !form.event_date || !form.location.trim() || (!isEditing && !form.url.trim()) || submitting}
              flex={1}
            >
              {submitting ? (isEditing ? "Saving..." : "Submitting...") : isEditing ? "Save changes" : "Send for review"}
            </AppButton>
          </AppButtonRow>
        </AppCardBody>
      </AppCard>
    </MemberPageScaffold>
  );
};

export default CreateEvent;
